import React, { useEffect, useState } from "react";
import { useAuth } from "../../components/context/Auth";
import styles from "./ProductionAndSales.module.css";
import {
  CancelIcon2,
  NewManagerCreateIcon,
  Spinner,
} from "../../components/icons/Icon";
import Button from "../../components/updatedComponents/Dealers/Button";
import { useNavigate } from "react-router-dom";
import fetchAcTokenData from "../../APIsControll/fetchAcTokenData";
import ComponentOtpVerify from "../../components/updatedComponents/Settings/ComponentOtpVerify";
import { ModalWrapper } from "../../exports";

const ProductionAndSales = ({
  otpActoken,
  setOtpAcToken,
  managerId,
  deviceId,
  iPInfo,
  browserName,
  operatinySystem,
  show,
}) => {
  const { mode } = useAuth();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [settings, setSettings] = useState({
    productionPaymentEnabled: false,
    productionPercentage: 0,
    partialPaymentEnabled: false,
  });

  const [loading, setLoading] = useState(false);
  const [productionData, setProductionData] = useState([]);

  const OTP_LENGTH = 6;
  const [adminOtp, setAdminOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [Otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));

  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

  const handleToggle = (field) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePercentageChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setSettings((prev) => ({
        ...prev,
        productionPercentage: "",
      }));
      return;
    }

    const num = Number(value);

    if (num >= 0 && num <= 100) {
      setSettings((prev) => ({
        ...prev,
        productionPercentage: num,
      }));
    }
  };

  const mapApiSettingsToState = (apiSettings) => {
    if (!apiSettings) return null;
    return {
      productionPaymentEnabled: !!apiSettings.productionPayRequired,
      productionPercentage:
        apiSettings.minRequiredPay === null ||
        apiSettings.minRequiredPay === undefined
          ? ""
          : apiSettings.minRequiredPay,
      partialPaymentEnabled: !!apiSettings.partPaymentCollection,
    };
  };

  // ─── Fetch settings (GET) ──────────────────────────────────────────────────
  const getProductionData = async () => {
    try {
      setLoading(true);

      const payload = {
        managerId,
      };

      const result = await fetchAcTokenData(
        "admin/productionAndSales",
        payload,
        otpActoken,
      );

      if (result?.status === "success") {
        setOtpAcToken(result?.actoken);

        const mapped = mapApiSettingsToState(result?.data?.productionAndSales);
        if (mapped) {
          setSettings(mapped);
        }

        setProductionData(result?.data);
      } else {
        show(result?.msg || "Failed to load production & sales Data");
      }
    } catch (error) {
      console.error("production & sales error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId, iPInfo, deviceId, browserName, operatinySystem]);

  // ─── Build the common payload used for both "send otp" and "verify otp" ───
  const buildPayload = (withOtp = false) => {
    const payload = {
      managerId,
      deviceId: deviceId,
      ip: iPInfo?.ip,
      browser: browserName,
      os: operatinySystem,
      city: iPInfo?.city,
      state: iPInfo?.region,
      country: iPInfo?.country_name,
      productionPayRequired: settings.productionPaymentEnabled,
      minRequiredPay: settings.productionPercentage,
      partPaymentCollection: settings.partialPaymentEnabled,
      voucherSettingId: productionData?._id || "",
    };

    if (withOtp) {
      payload.adminEmailOtp = adminOtp.join("");
      payload.emailOtp = Otp.join("");
    }

    return payload;
  };

  const sendOtp = async () => {
    // if (!settings.productionPaymentEnabled) {
    //   show("Please enable require payment for production");
    //   return;
    // }

    const isPercentageMissing =
      settings.productionPercentage === "" ||
      settings.productionPercentage === null ||
      settings.productionPercentage === undefined ||
      settings.productionPercentage <= 0;

    if (settings.productionPaymentEnabled && isPercentageMissing) {
      show("Please pass any percentage");
      return;
    }

    try {
      setSaving(true);

      const payload = buildPayload(false);

      const result = await fetchAcTokenData(
        "admin/addUpProductionAndSales",
        payload,
        otpActoken,
      );

      if (result?.status === "success") {
        // backend usually returns a fresh actoken even at the "otp sent" stage
        if (result?.actoken) {
          setOtpAcToken(result?.actoken);
        }
        // reset any stale otp values before opening the modal
        setAdminOtp(Array(OTP_LENGTH).fill(""));
        setOtp(Array(OTP_LENGTH).fill(""));
        setIsOtpModalOpen(true);
      } else {
        show(result?.msg || "Failed to send OTP");
      }
    } catch (error) {
      console.error("send otp error:", error);
      show("Something went wrong while sending OTP");
    } finally {
      setSaving(false);
    }
  };

  const handleCloseOtpModal = () => {
    setIsOtpModalOpen(false);
  };

  // ─── Step 2: Inside modal, click "Verify" -> hit the SAME API again WITH otp
  const createData = async () => {
    try {
      setVerifying(true);

      const payload = buildPayload(true);

      const result = await fetchAcTokenData(
        "admin/addUpProductionAndSales",
        payload,
        otpActoken,
      );

      if (result?.status === "success") {
        // set the new actoken returned after successful otp verification
        setOtpAcToken(result?.actoken);

        // if the verify response already contains fresh settings, map + use them
        const mapped = mapApiSettingsToState(result?.data?.productionAndSales);
        if (mapped) {
          setSettings(mapped);
        }
        if (result?.data) {
          setProductionData(result?.data);
        }

        setIsOtpModalOpen(false);

        // re-fetch settings from the GET api to make sure UI reflects saved data
        await getProductionData();
      } else {
        show(result?.msg || "OTP verification failed");
      }
    } catch (error) {
      console.error("verify otp error:", error);
      show("Something went wrong while verifying OTP");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className={`${styles.root} ${mode ? styles.dark : styles.light}`}>
      <ModalWrapper
        isOpen={isOtpModalOpen}
        onClose={handleCloseOtpModal}
        flag="alwaysopen"
      >
        <ComponentOtpVerify
          mode={mode}
          createData={createData}
          adminOtp={adminOtp}
          setAdminOtp={setAdminOtp}
          Otp={Otp}
          setOtp={setOtp}
          loading={verifying}
          onClose={handleCloseOtpModal}
        />
      </ModalWrapper>
      {loading ? (
        <div className={styles.loaderContainer}>
          <Spinner size="15" />
        </div>
      ) : (
        <>
          <div className={styles.nm_form}>
            <div className={styles.nm_form_section}>
              <div
                className={`${styles.settingsCard} ${
                  mode ? styles.settingsCardDark : ""
                }`}
              >
                {/* Production Payment */}

                <div className={styles.settingRow}>
                  <div className={styles.settingLeft}>
                    <div>
                      <div
                        className={`${styles.settingTitle} ${
                          mode ? styles.settingTitleDark : ""
                        }`}
                      >
                        Production Payment Requirement
                      </div>

                      <div className={styles.settingDescription}>
                        Customer must pay before production starts.
                      </div>
                    </div>

                    {settings?.productionPaymentEnabled && (
                      <div className={styles.percentInputWrapper}>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={settings.productionPercentage}
                          onChange={handlePercentageChange}
                          disabled={!settings.productionPaymentEnabled}
                          className={`${styles.percentInput} ${
                            mode ? styles.percentInputDark : ""
                          }`}
                        />

                        <span className={styles.percentSymbol}>%</span>
                      </div>
                    )}
                  </div>

                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={settings.productionPaymentEnabled}
                      onChange={() => handleToggle("productionPaymentEnabled")}
                    />
                    <span className={styles.toggleSlider} />
                  </label>
                </div>

                <div className={styles.divider} />

                {/* Partial Payment */}

                <div className={styles.settingRow}>
                  <div>
                    <div
                      className={`${styles.settingTitle} ${
                        mode ? styles.settingTitleDark : ""
                      }`}
                    >
                      Partial Payment Collection
                    </div>

                    <div className={styles.settingDescription}>
                      Allow customers to pay in multiple transactions.
                    </div>
                  </div>

                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={settings.partialPaymentEnabled}
                      onChange={() => handleToggle("partialPaymentEnabled")}
                    />
                    <span className={styles.toggleSlider} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div
        className={`${styles.nm_footer} ${mode ? styles.nm_footer_dark : ""}`}
      >
        <div className={styles.nm_footer_2}>
          <Button
            buttonIcon={<CancelIcon2 />}
            buttonText="CANCEL"
            buttonBgColor="#000"
            buttonTextColor="#fff"
            buttonBorder="none"
            buttonClick={() => navigate(-1)}
          />

          <Button
            buttonIcon={<NewManagerCreateIcon />}
            buttonText={saving ? "Sending Otp..." : "Next"}
            buttonBgColor="#4164E3"
            buttonTextColor="#fff"
            buttonBorder="none"
            disabled={saving}
            buttonClick={sendOtp}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductionAndSales;

// ----------------------- Discount Add -------------------------
// import React, { useEffect, useState } from "react";
// import { useAuth } from "../../components/context/Auth";
// import styles from "./ProductionAndSales.module.css";
// import {
//   CancelIcon2,
//   NewManagerCreateIcon,
//   Spinner,
// } from "../../components/icons/Icon";
// import Button from "../../components/updatedComponents/Dealers/Button";
// import { useNavigate } from "react-router-dom";
// import fetchAcTokenData from "../../APIsControll/fetchAcTokenData";
// import ComponentOtpVerify from "../../components/updatedComponents/Settings/ComponentOtpVerify";
// import { ModalWrapper } from "../../exports";
// import DropDown2 from "../../components/updatedComponents/FormComponents/Dropdown2";

// const OTP_LENGTH = 6;

// const DISCOUNT_TYPE_OPTIONS = [
//   // { name: "Fixed", value: "fixed" },
//   // { name: "Range", value: "range" },
//   { name: "Percentage", value: "percentage" },
//   { name: "Percentage range", value: "percentage range" },
// ];

// const ProductionAndSales = ({
//   otpActoken,
//   setOtpAcToken,
//   managerId,
//   deviceId,
//   iPInfo,
//   browserName,
//   operatinySystem,
//   show,
// }) => {
//   const { mode } = useAuth();
//   const navigate = useNavigate();

//   const [saving, setSaving] = useState(false);
//   const [verifying, setVerifying] = useState(false);

//   const [settings, setSettings] = useState({
//     productionPaymentEnabled: false,
//     productionPercentage: 0,
//     partialPaymentEnabled: false,
//     // discount
//     discountEnabled: false,
//     discountType: "percentage", // "fixed" | "range" | "percentage"
//     discountValue: 0,
//     discountMin: "",
//     discountMax: "",
//     // discount approval
//     discountApproval: false,
//     discountApprovalType: "percentage", // "fixed" | "range" | "percentage"
//     discountApprovalValue: 0,
//     discountApprovalMin: "",
//     discountApprovalMax: "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [productionData, setProductionData] = useState([]);

//   const [adminOtp, setAdminOtp] = useState(Array(OTP_LENGTH).fill(""));
//   const [Otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));

//   const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

//   const handleToggle = (field) => {
//     setSettings((prev) => ({
//       ...prev,
//       [field]: !prev[field],
//     }));
//   };

//   const handlePercentageChange = (e) => {
//     const value = e.target.value;

//     if (value === "") {
//       setSettings((prev) => ({ ...prev, productionPercentage: "" }));
//       return;
//     }

//     const num = Number(value);

//     if (num >= 0 && num <= 100) {
//       setSettings((prev) => ({ ...prev, productionPercentage: num }));
//     }
//   };

//   // ─── Discount handlers ─────────────────────────────────────────────────────
//   const handleDiscountChange = (field, value) => {
//     if (value === "") {
//       setSettings((prev) => ({ ...prev, [field]: "" }));
//       return;
//     }

//     const num = Number(value);
//     if (Number.isNaN(num) || num < 0) return;

//     // percentage can't exceed 100
//     if (settings.discountType === "percentage" && num > 100) return;

//     setSettings((prev) => ({ ...prev, [field]: num }));
//   };

//   // DropDown2 onSelect: accepts either an option object or a plain string
//   const handleDiscountTypeChange = (option) => {
//     const type = typeof option === "string" ? option : option?.value;
//     if (!type) return;

//     // reset values when the type changes so old values don't leak across types
//     setSettings((prev) => ({
//       ...prev,
//       discountType: type,
//       discountValue: "",
//       discountMin: "",
//       discountMax: "",
//     }));
//   };

//   const handleApprovalDiscountChange = (field, value) => {
//     if (value === "") {
//       setSettings((prev) => ({ ...prev, [field]: "" }));
//       return;
//     }

//     const num = Number(value);
//     if (Number.isNaN(num) || num < 0) return;

//     // percentage can't exceed 100
//     if (settings.discountApprovalType === "percentage" && num > 100) return;

//     setSettings((prev) => ({ ...prev, [field]: num }));
//   };

//   const handleDiscountApprovalTypeChange = (option) => {
//     const type = typeof option === "string" ? option : option?.value;
//     if (!type) return;

//     // reset values when the type changes so old values don't leak across types
//     setSettings((prev) => ({
//       ...prev,
//       discountApprovalType: type,
//       discountApprovalValue: "",
//       discountApprovalMin: "",
//       discountApprovalMax: "",
//     }));
//   };

//   const mapApiSettingsToState = (apiSettings) => {
//     if (!apiSettings) return null;
//     return {
//       productionPaymentEnabled: !!apiSettings.productionPayRequired,
//       productionPercentage:
//         apiSettings.minRequiredPay === null ||
//         apiSettings.minRequiredPay === undefined
//           ? ""
//           : apiSettings.minRequiredPay,
//       partialPaymentEnabled: !!apiSettings.partPaymentCollection,
//       // discount
//       discountEnabled: !!apiSettings.discountEnabled,
//       discountType: apiSettings.discountType || "fixed",
//       discountValue: apiSettings.discountValue ?? "",
//       discountMin: apiSettings.discountMin ?? "",
//       discountMax: apiSettings.discountMax ?? "",

//       // discount approval
//       discountApproval: !!apiSettings.discountApproval,
//       discountApprovalType: apiSettings.discountApprovalType || "fixed",
//       discountApprovalValue: apiSettings.discountApprovalValue ?? "",
//       discountApprovalMin: apiSettings.discountApprovalMin ?? "",
//       discountApprovalMax: apiSettings.discountApprovalMax ?? "",
//     };
//   };

//   // ─── Fetch settings (GET) ──────────────────────────────────────────────────
//   const getProductionData = async () => {
//     try {
//       setLoading(true);

//       const payload = { managerId };

//       const result = await fetchAcTokenData(
//         "admin/productionAndSales",
//         payload,
//         otpActoken,
//       );

//       if (result?.status === "success") {
//         setOtpAcToken(result?.actoken);

//         const mapped = mapApiSettingsToState(result?.data?.productionAndSales);
//         if (mapped) {
//           setSettings(mapped);
//         }

//         setProductionData(result?.data);
//       } else {
//         show(result?.msg || "Failed to load production & sales Data");
//       }
//     } catch (error) {
//       console.error("production & sales error:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     getProductionData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [managerId, iPInfo, deviceId, browserName, operatinySystem]);

//   // ─── Build the common payload used for both "send otp" and "verify otp" ───
//   const buildPayload = (withOtp = false) => {
//     const payload = {
//       managerId,
//       deviceId: deviceId,
//       ip: iPInfo?.ip,
//       browser: browserName,
//       os: operatinySystem,
//       city: iPInfo?.city,
//       state: iPInfo?.region,
//       country: iPInfo?.country_name,
//       productionPayRequired: settings.productionPaymentEnabled,
//       minRequiredPay: settings.productionPercentage,
//       partPaymentCollection: settings.partialPaymentEnabled,
//       // discount
//       discountEnabled: settings.discountEnabled,
//       discountType: settings.discountType,
//       discountValue: settings.discountValue === "" ? 0 : settings.discountValue,
//       discountMin: settings.discountMin === "" ? 0 : settings.discountMin,
//       discountMax: settings.discountMax === "" ? 0 : settings.discountMax,

//       // discount approval
//       discountApproval: settings.discountApproval,
//       discountApprovalType: settings.discountApprovalType,
//       discountApprovalValue:
//         settings.discountApprovalValue === ""
//           ? 0
//           : settings.discountApprovalValue,
//       discountApprovalMin:
//         settings.discountApprovalMin === "" ? 0 : settings.discountApprovalMin,
//       discountApprovalMax:
//         settings.discountApprovalMax === "" ? 0 : settings.discountApprovalMax,

//       voucherSettingId: productionData?._id || "",
//     };

//     if (withOtp) {
//       payload.adminEmailOtp = adminOtp.join("");
//       payload.emailOtp = Otp.join("");
//     }

//     return payload;
//   };

//   // ─── Step 1: validate + send OTP ───────────────────────────────────────────
//   const sendOtp = async () => {
//     const isPercentageMissing =
//       settings.productionPercentage === "" ||
//       settings.productionPercentage === null ||
//       settings.productionPercentage === undefined ||
//       settings.productionPercentage <= 0;

//     if (settings.productionPaymentEnabled && isPercentageMissing) {
//       show("Please pass any percentage");
//       return;
//     }

//     // ─── Discount validation ───
//     // if (settings.discountEnabled) {
//     //   if (settings.discountType === "range") {
//     //     const min = Number(settings.discountMin);
//     //     const max = Number(settings.discountMax);
//     //     if (settings.discountMin === "" || settings.discountMax === "") {
//     //       show("Please enter discount min and max");
//     //       return;
//     //     }
//     //     if (min >= max) {
//     //       show("Discount max must be greater than min");
//     //       return;
//     //     }
//     //   } else if (
//     //     settings.discountValue === "" ||
//     //     Number(settings.discountValue) <= 0
//     //   ) {
//     //     show("Please enter discount value");
//     //     return;
//     //   }
//     // }

//     try {
//       setSaving(true);

//       const payload = buildPayload(false);

//       const result = await fetchAcTokenData(
//         "admin/addUpProductionAndSales",
//         payload,
//         otpActoken,
//       );

//       if (result?.status === "success") {
//         // backend usually returns a fresh actoken even at the "otp sent" stage
//         if (result?.actoken) {
//           setOtpAcToken(result?.actoken);
//         }
//         // reset any stale otp values before opening the modal
//         setAdminOtp(Array(OTP_LENGTH).fill(""));
//         setOtp(Array(OTP_LENGTH).fill(""));
//         setIsOtpModalOpen(true);
//       } else {
//         show(result?.msg || "Failed to send OTP");
//       }
//     } catch (error) {
//       console.error("send otp error:", error);
//       show("Something went wrong while sending OTP");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const handleCloseOtpModal = () => {
//     setIsOtpModalOpen(false);
//   };

//   // ─── Step 2: inside modal, verify -> hit the SAME API again WITH otp ───────
//   const createData = async () => {
//     try {
//       setVerifying(true);

//       const payload = buildPayload(true);

//       const result = await fetchAcTokenData(
//         "admin/addUpProductionAndSales",
//         payload,
//         otpActoken,
//       );

//       if (result?.status === "success") {
//         // set the new actoken returned after successful otp verification
//         setOtpAcToken(result?.actoken);

//         // if the verify response already contains fresh settings, map + use them
//         const mapped = mapApiSettingsToState(result?.data?.productionAndSales);
//         if (mapped) {
//           setSettings(mapped);
//         }
//         if (result?.data) {
//           setProductionData(result?.data);
//         }

//         setIsOtpModalOpen(false);

//         // re-fetch settings from the GET api to make sure UI reflects saved data
//         await getProductionData();
//       } else {
//         show(result?.msg || "OTP verification failed");
//       }
//     } catch (error) {
//       console.error("verify otp error:", error);
//       show("Something went wrong while verifying OTP");
//     } finally {
//       setVerifying(false);
//     }
//   };

//   const selectedDiscountLabel =
//     DISCOUNT_TYPE_OPTIONS.find((o) => o.value === settings.discountType)
//       ?.name || "Select type";

//   const inputClass = `${styles.percentInput} ${
//     mode ? styles.percentInputDark : ""
//   }`;

//   return (
//     <div className={`${styles.root} ${mode ? styles.dark : styles.light}`}>
//       <ModalWrapper
//         isOpen={isOtpModalOpen}
//         onClose={handleCloseOtpModal}
//         flag="alwaysopen"
//       >
//         <ComponentOtpVerify
//           mode={mode}
//           createData={createData}
//           adminOtp={adminOtp}
//           setAdminOtp={setAdminOtp}
//           Otp={Otp}
//           setOtp={setOtp}
//           loading={verifying}
//           onClose={handleCloseOtpModal}
//         />
//       </ModalWrapper>

//       {loading ? (
//         <div className={styles.loaderContainer}>
//           <Spinner size="15" />
//         </div>
//       ) : (
//         <div className={styles.nm_form}>
//           <div className={styles.nm_form_section}>
//             <div
//               className={`${styles.settingsCard} ${
//                 mode ? styles.settingsCardDark : ""
//               }`}
//             >
//               {/* Production Payment */}
//               <div className={styles.settingRow}>
//                 <div className={styles.settingLeft}>
//                   <div>
//                     <div
//                       className={`${styles.settingTitle} ${
//                         mode ? styles.settingTitleDark : ""
//                       }`}
//                     >
//                       Production Payment Requirement
//                     </div>

//                     <div className={styles.settingDescription}>
//                       Customer must pay before production starts.
//                     </div>
//                   </div>

//                   {settings.productionPaymentEnabled && (
//                     <div className={styles.percentInputWrapper}>
//                       <input
//                         type="number"
//                         min="0"
//                         max="100"
//                         value={settings.productionPercentage}
//                         onChange={handlePercentageChange}
//                         className={inputClass}
//                       />
//                       <span className={styles.percentSymbol}>%</span>
//                     </div>
//                   )}
//                 </div>

//                 <label className={styles.toggle}>
//                   <input
//                     type="checkbox"
//                     checked={settings.productionPaymentEnabled}
//                     onChange={() => handleToggle("productionPaymentEnabled")}
//                   />
//                   <span className={styles.toggleSlider} />
//                 </label>
//               </div>

//               <div className={styles.divider} />

//               {/* Partial Payment */}
//               <div className={styles.settingRow}>
//                 <div>
//                   <div
//                     className={`${styles.settingTitle} ${
//                       mode ? styles.settingTitleDark : ""
//                     }`}
//                   >
//                     Partial Payment Collection
//                   </div>

//                   <div className={styles.settingDescription}>
//                     Allow customers to pay in multiple transactions.
//                   </div>
//                 </div>

//                 <label className={styles.toggle}>
//                   <input
//                     type="checkbox"
//                     checked={settings.partialPaymentEnabled}
//                     onChange={() => handleToggle("partialPaymentEnabled")}
//                   />
//                   <span className={styles.toggleSlider} />
//                 </label>
//               </div>

//               <div className={styles.divider} />

//               {/* Discount */}
//               <div className={styles.settingRow}>
//                 <div className={styles.settingLeft}>
//                   <div>
//                     <div
//                       className={`${styles.settingTitle} ${
//                         mode ? styles.settingTitleDark : ""
//                       }`}
//                     >
//                       Discount
//                     </div>

//                     <div className={styles.settingDescription}>
//                       Allow discount on production and sales to all.
//                     </div>
//                   </div>
//                 </div>

//                 <label className={styles.toggle}>
//                   <input
//                     type="checkbox"
//                     checked={settings.discountEnabled}
//                     onChange={() => handleToggle("discountEnabled")}
//                   />
//                   <span className={styles.toggleSlider} />
//                 </label>
//               </div>

//               {settings.discountEnabled && (
//                 <div className={styles.discountControls}>
//                   <div className={styles.discountDropdown}>
//                     <DropDown2
//                       options={DISCOUNT_TYPE_OPTIONS}
//                       // shown until the user picks an option, so it displays
//                       // the type loaded from the API (or the default "Fixed")
//                       listLabel={selectedDiscountLabel}
//                       onSelect={handleDiscountTypeChange}
//                     />
//                   </div>

//                   {settings.discountType === "range" ? (
//                     <>
//                       <div className={styles.valueInputWrapper}>
//                         <input
//                           type="number"
//                           min="0"
//                           placeholder="Min"
//                           value={settings.discountMin}
//                           onChange={(e) =>
//                             handleDiscountChange("discountMin", e.target.value)
//                           }
//                           className={inputClass}
//                         />
//                       </div>

//                       <span className={styles.rangeSeparator}>to</span>

//                       <div className={styles.valueInputWrapper}>
//                         <input
//                           type="number"
//                           min="0"
//                           placeholder="Max"
//                           value={settings.discountMax}
//                           onChange={(e) =>
//                             handleDiscountChange("discountMax", e.target.value)
//                           }
//                           className={inputClass}
//                         />
//                       </div>
//                     </>
//                   ) : (
//                     <div className={styles.valueInputWrapper}>
//                       <input
//                         type="number"
//                         min="0"
//                         max={
//                           settings.discountType === "percentage"
//                             ? 100
//                             : undefined
//                         }
//                         placeholder="00"
//                         value={settings.discountValue}
//                         onChange={(e) =>
//                           handleDiscountChange("discountValue", e.target.value)
//                         }
//                         className={inputClass}
//                       />

//                       {settings.discountType === "percentage" && (
//                         <span className={styles.percentSymbol}>%</span>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               )}

//               <div className={styles.divider} />

//               <div className={styles.settingRow}>
//                 <div className={styles.settingLeft}>
//                   <div>
//                     <div
//                       className={`${styles.settingTitle} ${
//                         mode ? styles.settingTitleDark : ""
//                       }`}
//                     >
//                       Discount on approval
//                     </div>

//                     <div className={styles.settingDescription}>
//                       Allow discount on production and sales by approval for
//                       stockist, distributor, sale and more.
//                     </div>
//                   </div>
//                 </div>

//                 <label className={styles.toggle}>
//                   <input
//                     type="checkbox"
//                     checked={settings.discountApproval}
//                     onChange={() => handleToggle("discountApproval")}
//                   />
//                   <span className={styles.toggleSlider} />
//                 </label>
//               </div>

//               {settings.discountApproval && (
//                 <div className={styles.discountControls}>
//                   <div className={styles.discountDropdown}>
//                     <DropDown2
//                       options={DISCOUNT_TYPE_OPTIONS}
//                       listLabel={selectedDiscountLabel}
//                       onSelect={handleDiscountApprovalTypeChange}
//                     />
//                   </div>

//                   {settings.discountApprovalType === "range" ? (
//                     <>
//                       <div className={styles.valueInputWrapper}>
//                         <input
//                           type="number"
//                           min="0"
//                           placeholder="Min"
//                           value={settings.discountApprovalMin}
//                           onChange={(e) =>
//                             handleApprovalDiscountChange(
//                               "discountApprovalMin",
//                               e.target.value,
//                             )
//                           }
//                           className={inputClass}
//                         />
//                       </div>

//                       <span className={styles.rangeSeparator}>to</span>

//                       <div className={styles.valueInputWrapper}>
//                         <input
//                           type="number"
//                           min="0"
//                           placeholder="Max"
//                           value={settings.discountApprovalMax}
//                           onChange={(e) =>
//                             handleApprovalDiscountChange(
//                               "discountApprovalMax",
//                               e.target.value,
//                             )
//                           }
//                           className={inputClass}
//                         />
//                       </div>
//                     </>
//                   ) : (
//                     <div className={styles.valueInputWrapper}>
//                       <input
//                         type="number"
//                         min="0"
//                         max={
//                           settings.discountApprovalType === "percentage"
//                             ? 100
//                             : undefined
//                         }
//                         placeholder="00"
//                         value={settings.discountApprovalValue}
//                         onChange={(e) =>
//                           handleApprovalDiscountChange(
//                             "discountApprovalValue",
//                             e.target.value,
//                           )
//                         }
//                         className={inputClass}
//                       />

//                       {settings.discountApprovalType === "percentage" && (
//                         <span className={styles.percentSymbol}>%</span>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       <div
//         className={`${styles.nm_footer} ${mode ? styles.nm_footer_dark : ""}`}
//       >
//         <div className={styles.nm_footer_2}>
//           <Button
//             buttonIcon={<CancelIcon2 />}
//             buttonText="CANCEL"
//             buttonBgColor="#000"
//             buttonTextColor="#fff"
//             buttonBorder="none"
//             buttonClick={() => navigate(-1)}
//           />

//           <Button
//             buttonIcon={<NewManagerCreateIcon />}
//             buttonText={saving ? "Sending Otp..." : "Next"}
//             buttonBgColor="#4164E3"
//             buttonTextColor="#fff"
//             buttonBorder="none"
//             disabled={saving}
//             buttonClick={sendOtp}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductionAndSales;
